import { lazy } from 'react';

// project imports
import MainLayout from 'layout/MainLayout';
import Loadable from 'ui-component/Loadable';
import AuthGuard from 'utils/route-guard/AuthGuard';
import ClinicAccessGuard from 'utils/route-guard/ClinicAccessGuard';

// sample page routing
const Homepage = Loadable(lazy(() => import('views/homepage')));

const PatientNewPage = Loadable(lazy(() => import('views/patientNew')));
const PatientList = Loadable(lazy(() => import('views/patientList')));
const PatientEdit = Loadable(lazy(() => import('views/patientEdit')));
const UserNew = Loadable(lazy(() => import('views/userManagement/userNew')));
const VitalsAlerts = Loadable(lazy(() => import('views/vitalsAlerts')));
const Reports = Loadable(lazy(() => import('views/reports')));
const BreakdownPatientList = Loadable(lazy(() => import('views/reports/breakdown')));
const ClinicTasks = Loadable(lazy(() => import('views/task')));
const UserManagement = Loadable(lazy(() => import('views/userManagement')));
const ProductivityGoals = Loadable(lazy(() => import('views/productivityGoals')));
const TeamPerformance = Loadable(lazy(() => import('views/teamPerformance')));
const UserDetail = Loadable(lazy(() => import('views/userManagement/userDetails')));
const ClinicDetails = Loadable(lazy(() => import('views/clinicSettings/clinicDetails')));
const ProtocolsTemplateEditor = Loadable(lazy(() => import('views/protocolsTemplate/protocolsTemplateEditor')));
const ProtocolsTemplateList = Loadable(lazy(() => import('views/protocolsTemplate/protocolsTemplateList')));
const CustomProgrammaticTaskList = Loadable(lazy(() => import('views/customProgrammaticTasks/list')));
const CustomProgrammaticTaskEditor = Loadable(lazy(() => import('views/customProgrammaticTasks/instance')));
const ProgrammaticTaskPreview = Loadable(lazy(() => import('views/clinicSettings/programmaticTaskPreview')));
const ProgrammaticTasks = Loadable(lazy(() => import('views/clinicSettings/programmaticTasks')));
const OrganizationTeamsManagement = Loadable(lazy(() => import('views/teams/organizationTeamManagement')));
const MedHXSubmissionManagement = Loadable(lazy(() => import('views/medHXSubmissionManagement')));
const ImporterTool = Loadable(lazy(() => import('views/importerTool/importer')));
const OrgSettings = Loadable(lazy(() => import('views/orgSettings')));
const NotFoundPage = Loadable(lazy(() => import('views/access/notFoundPage')));

// Clinic-scoped pages render only when the account has a grant for the URL's clinic.
const clinicPage = (element) => <ClinicAccessGuard>{element}</ClinicAccessGuard>;

// ==============================|| MAIN ROUTING ||============================== //

const MainRoutes = {
    path: '/',
    element: (
        <AuthGuard>
            <MainLayout />
        </AuthGuard>
    ),
    children: [
        {
            path: '/',
            element: <Homepage />
        },
        {
            path: '/clinic/:clinicId/patients',
            element: clinicPage(<PatientList />)
        },
        {
            path: '/clinic/:clinicId/patient/new',
            element: clinicPage(<PatientNewPage />)
        },
        {
            path: '/clinic/:clinicId/patient/:patientId/edit/:tab/:subTab',
            element: clinicPage(<PatientEdit />)
        },
        {
            path: '/user/new',
            element: <UserNew />
        },
        {
            path: '/clinic/:clinicId/vitals/alerts',
            element: clinicPage(<VitalsAlerts />)
        },
        {
            path: '/clinic/:clinicId/reports',
            element: clinicPage(<Reports />)
        },
        {
            path: '/clinic/:clinicId/reports/breakdown/:type',
            element: clinicPage(<BreakdownPatientList />)
        },
        {
            path: '/clinic/:clinicId/tasks',
            element: clinicPage(<ClinicTasks />)
        },
        {
            path: '/user/:userId/detail',
            element: <UserDetail />
        },
        {
            path: '/users',
            element: <UserManagement />
        },
        {
            path: '/clinic/:clinicId/details',
            element: clinicPage(<ClinicDetails />)
        },
        {
            path: '/protocols/template/:templateId/edit',
            element: <ProtocolsTemplateEditor />
        },
        {
            path: '/protocols/templates',
            element: <ProtocolsTemplateList />
        },
        {
            path: '/custom-programmatic-task/list',
            element: <CustomProgrammaticTaskList />
        },
        {
            path: '/custom-programmatic-task/:instanceId/edit',
            element: <CustomProgrammaticTaskEditor />
        },
        {
            path: '/clinic/:clinicId/programmatic-task-preview/:previewRunId',
            element: clinicPage(<ProgrammaticTaskPreview />)
        },
        {
            path: '/clinic/:clinicId/programmatic-tasks',
            element: clinicPage(<ProgrammaticTasks />)
        },
        {
            path: '/organization-teams-management',
            element: <OrganizationTeamsManagement />
        },
        {
            path: '/productivity-goals',
            element: <ProductivityGoals />
        },
        {
            path: '/team-performance',
            element: <TeamPerformance />
        },
        {
            path: '/import-report-submission-management',
            element: <MedHXSubmissionManagement />
        },
        {
            path: '/clinic/:clinicId/importer-tool',
            element: clinicPage(<ImporterTool />)
        },
        {
            path: '/org-settings',
            element: <OrgSettings />
        },
        {
            path: '*',
            element: <NotFoundPage />
        }
    ]
};

export default MainRoutes;
